import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils-client"

const buttonVariants = cva(
  "cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap text-xs font-medium font-mono transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive rounded-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "border border-primary bg-none underline-offset-1 hover:underline",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-primary/10 dark:hover:bg-primary/75",
        ghost:
          "hover:bg-primary/10 dark:hover:bg-primary/50 rounded-none",
        link: "text-foreground underline-offset-1 hover:underline rounded-none",
      },
      size: {
        default: "h-[36px] px-2 py-2 has-[>svg]:px-2",
        sm: "h-8 gap-1.5 px-2 has-[>svg]:px-2",
        lg: "h-12 px-4 has-[>svg]:px-4",
        icon: "size-[36px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  // Development-only render diagnostics - TEMPORARILY DISABLED FOR TESTING
  // const renderCountRef = React.useRef(0)
  // renderCountRef.current += 1
  // if (process.env.NODE_ENV === "development" && renderCountRef.current <= 10) {
  //   // Log only first few renders per instance to avoid console noise
  //   // Includes key props that influence className composition
  //   console.debug("Button render", {
  //     count: renderCountRef.current,
  //     variant,
  //     size,
  //     asChild,
  //   })
  // }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
